/**
 * Business Model slide (5) — client-side parser + assembler + defaults.
 *
 * Iter55 (Feb 2026). Mirrors `lib/marketSize.js`.
 *
 * The slide's `content` string on the wire is the canonical shape the
 * server-side `pitch_business_model.parse_business_model` also understands,
 * so preview / PPTX / play-deck / premium-rewrite all consume the same
 * source without translation.
 */

/** Canonical field order + display metadata. `key` is stable and used by
 *  the AI redraft endpoint; `label` is what shows on-screen and also acts
 *  as the parse key inside the content string. */
export const BUSINESS_MODEL_FIELDS = [
  {
    key: "customer_segments",
    label: "Customer Segments",
    subtitle: "Who are your target users or buyers?",
    // Phosphor Icons React import handles the actual glyph.
    icon: "UsersThree",
  },
  {
    key: "value_proposition",
    label: "Value Proposition",
    subtitle: "What specific problem are you solving for them, and why will they pay for it?",
    icon: "Diamond",
  },
  {
    key: "revenue_streams",
    label: "Revenue Streams",
    subtitle: "What are your pricing strategies? (e.g., subscription, one-time sales, licensing, freemium)",
    icon: "Stack",
  },
  {
    key: "cost_structure",
    label: "Cost Structure",
    subtitle: "What are your primary expenses to operate and scale the business?",
    icon: "Calculator",
  },
  {
    key: "channels",
    label: "Channels & Customer Acquisition",
    subtitle: "How will you reach your customers and convince them to buy?",
    icon: "MegaphoneSimple",
  },
];

/** Per-field word cap. Matches backend `WORD_CAP_PER_FIELD`. Slightly
 *  larger than we visually recommend so the founder has a bit of headroom
 *  before the counter turns red. */
export const WORD_CAP_PER_FIELD = 32;

/** Word count of a string. Empty / whitespace → 0. */
export function countWords(s) {
  if (!s) return 0;
  const trimmed = s.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

const LABELS = BUSINESS_MODEL_FIELDS.map((f) => f.label);

/** Parse the canonical content string into a full 5-row array (missing
 *  fields returned with `text=""` so the preview always has 5 rows).
 *  See `pitch_business_model.parse_business_model` for the mirror.
 */
export function parseBusinessModel(content) {
  const out = Object.fromEntries(LABELS.map((l) => [l, ""]));
  if (content) {
    const blocks = content.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
    for (const block of blocks) {
      const lines = block.split(/\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) continue;
      const header = lines[0];
      const match = LABELS.find((l) => l === header || l.toLowerCase() === header.toLowerCase());
      if (!match) continue;
      out[match] = lines.slice(1).join(" ").trim();
    }
  }
  return BUSINESS_MODEL_FIELDS.map((f) => ({
    key: f.key,
    label: f.label,
    subtitle: f.subtitle,
    icon: f.icon,
    text: out[f.label] || "",
  }));
}

/** Inverse of `parseBusinessModel`. Accepts either key-based (`customer_segments`)
 *  or label-based (`Customer Segments`) input. Empty fields are omitted so the
 *  stored content string stays tidy. */
export function assembleBusinessModel(fieldTexts) {
  const blocks = [];
  for (const f of BUSINESS_MODEL_FIELDS) {
    const raw = fieldTexts[f.key] ?? fieldTexts[f.label] ?? "";
    const text = (raw || "").trim();
    if (!text) continue;
    blocks.push(`${f.label}\n${text}`);
  }
  return blocks.join("\n\n");
}
